import angular from 'angular';
import { LicenseService } from '../../tn.licenses/license.service/license-service';
import { LocalProjects } from '../local-projects.service/localProjects';

newProject.$inject = [
    'order',
    'localProjects',
    'tnLicenseService',
    '$scope',
    '$state',
    '$filter',
];

export function newProject(
    order,
    localProjects: LocalProjects,
    licenseService: LicenseService,
    $scope,
    $state,
    $filter
): void {
    const vm = angular.extend(this, {
        order,
        orderLicenses: [],
        licenses: [],
        newProject: { name: null, domains: [], licenses: [] },
        selectedProjectID: null,
        projects: [],
        newDomain: '',
        allLicenses: getAllLicenses(),
        styleQuery: '',

        addDomain,
        removeDomain,
        trimDomain,
        addLicense,
        removeLicense,
        getActiveProject,
        projectIsSelected,
        searchStylesByName,
        step1IsComplete,
        step2IsComplete,
        step3IsComplete,
        submitProject,
        submitting: false,
    });

    init();

    function init() {
        // Add all new web licenses from the order
        const licenses = _makeWebOnly(angular.copy(vm.order.licenses));
        vm.orderLicenses.push(...licenses);
        // load the projects
        localProjects.getProjects().then((projects) => {
            vm.projects = projects;
        });
        // Calculate licenses when the project changes
        $scope.$watch('vm.selectedProjectID', function (newID) {
            if (newID !== null) {
                _updateLicenses();

                // Make sure we have the domains array on the object
                const project = vm.getActiveProject();
                if (!project.domains) {
                    project.domains = [];
                }
            }
        });
        $scope.$watch('vm.newProject.name', function (newName, oldName) {
            if (newName !== null && newName !== '' && oldName === null) {
                _updateLicenses();
            }
        });
    }

    function addDomain() {
        // Allow adding of first domain
        if (
            vm.newDomain !== null &&
            vm.newDomain !== '' &&
            vm.newProjectForm.newDomain.$valid
        ) {
            vm.getActiveProject().domains.push(vm.newDomain);
            vm.newDomain = '';
        } else if (vm.newDomain === null) {
            vm.newDomain = '';
        }
    }

    function removeDomain(index) {
        vm.getActiveProject().domains.splice(index, 1);
    }

    function trimDomain() {
        const newDomain = vm.newProjectForm.newDomain;
        if (newDomain.$viewValue[newDomain.$viewValue.length - 1] === '/') {
            vm.newDomain = newDomain.$viewValue.slice(
                0,
                newDomain.$viewValue.length - 1
            );
        }
        if (newDomain.$valid) {
            [
                'http://www.',
                'https://www.',
                'http://',
                'https://',
                'www.',
            ].forEach((bannedString) => {
                if (vm.newDomain.indexOf(bannedString) === 0) {
                    vm.newDomain = vm.newDomain.substring(bannedString.length);
                }
            });
        }
    }

    function addLicense() {
        if (vm.styleQuery !== null && typeof vm.styleQuery === 'object') {
            const license = angular.copy(vm.styleQuery);
            vm.licenses.push(license);
            getActiveProject().licenses.push(license);
            vm.styleQuery = '';
        } else if (vm.styleQuery === null) {
            vm.styleQuery = '';
        }
    }

    function removeLicense(license) {
        vm.licenses.splice(vm.licenses.indexOf(license), 1);
    }

    function projectIsSelected(project) {
        return parseInt(vm.selectedProjectID) === project.id;
    }

    function getActiveProject() {
        if (vm.selectedProjectID) {
            return vm.projects.filter(
                (project) => project.id === parseInt(vm.selectedProjectID)
            )[0];
        } else {
            return vm.newProject;
        }
    }

    function getAllLicenses() {
        return licenseService.getLicensesFromServer().then((licenses) => {
            const webLicenses = angular.copy(licenses);
            return _getLargestWebLicenseForStyle(_makeWebOnly(webLicenses));
        });
    }

    function _getLargestWebLicenseForStyle(licenses) {
        licenses.forEach((license) => {
            const duplicateLicenses = licenses.filter(
                (li) => li.style.id === license.style.id
            );
            if (duplicateLicenses.length > 1) {
                duplicateLicenses
                    .sort(function (a, b) {
                        return a.fontRights.web.qty - b.fontRights.web.qty;
                    })
                    .pop();
                duplicateLicenses.forEach((match) =>
                    licenses.splice(licenses.indexOf(match), 1)
                );
            }
        });
        return licenses;
    }

    function searchStylesByName(name) {
        return vm.allLicenses.then((allLicenses) => {
            name = name.toLowerCase();
            // Only show licensed fonts that are not yet in the project
            const currentStyles = vm.licenses.map(
                (license) => license.style.id
            );
            let licensePool = allLicenses.filter(
                (license) =>
                    currentStyles.indexOf(license.style.id) === -1 &&
                    license.fontRights.web &&
                    license.fontRights.web.active &&
                    !license.fontRights.web.self_hosted
            );

            // Sort licensePool by style name, we have to do this here because we limit in this function
            licensePool = $filter('orderBy')(licensePool, 'style.name');

            return licensePool.filter(
                (license) =>
                    license.style.name.toLowerCase().indexOf(name) !== -1
            );
        });
    }

    function step1IsComplete() {
        return (
            (vm.newProject.name !== null && vm.newProject.name !== '') ||
            vm.selectedProjectID !== null
        );
    }

    function step2IsComplete() {
        if (
            vm.newDomain !== null &&
            vm.newDomain !== '' &&
            vm.newProjectForm.newDomain.$valid
        ) {
            return true;
        }

        const project = vm.getActiveProject();
        if (project.domains) {
            return (
                project.domains.filter((domain) => domain !== '').length !== 0
            );
        }

        return false;
    }

    function step3IsComplete() {
        return (
            vm.licenses.length !== 0 ||
            (typeof vm.styleQuery === 'object' && vm.styleQuery !== null)
        );
    }

    function submitProject() {
        vm.submitting = true;
        const project = vm.getActiveProject();

        // Update license collection
        project.licenses.splice(0, project.licenses.length);
        project.licenses.push(...vm.licenses);
        // Add in extra license if applicable
        if (typeof vm.styleQuery === 'object' && vm.styleQuery !== null) {
            const license = angular.copy(vm.styleQuery);
            project.licenses.push(license);
        }

        // Add in extra domain if applicable
        if (vm.newDomain !== null && vm.newDomain !== '') {
            project.domains.push(vm.newDomain);
        }

        // Create a new project, or update the existing one
        localProjects.saveProject(project).then((result) => {
            $state.go('home.account.projects.project', {
                projectId: result.id,
            });
        });
    }

    function _makeWebOnly(licenses) {
        // Don't blow up when there's no licenses
        if (licenses === undefined) {
            return [];
        }

        licenses = licenses.filter(
            (license) =>
                license.fontRights.web &&
                !license.fontRights.web.self_hosted &&
                license.fontRights.web.active
        );
        licenses.forEach((license) => {
            Object.keys(license.fontRights).forEach((key) => {
                if (key !== 'web') {
                    delete license.fontRights[key];
                }
            });
        });
        return licenses;
    }

    function _updateLicenses() {
        vm.licenses.splice(0, vm.licenses.length);
        vm.licenses.push(...vm.orderLicenses, ...getActiveProject().licenses);
    }
}
